import { isEmpty, isNumber, isString, isUndefined, merge } from 'lodash-es';
import { type PropsWithChildren } from 'react';

import { isHoneycombTileCategoricalData } from './is-honeycomb-tile-categorical-data.js';
import { isHoneycombTileNumericData } from './is-honeycomb-tile-numeric-data.js';
import { isValidColorRule } from '../../core/slots/ColorRule.js';
import {
  DEFAULT_HONEYCOMB_CATEGORICAL_COLOR_PALETTE,
  DEFAULT_HONEYCOMB_CONFIG,
  DEFAULT_TEXT_SIZE,
} from '../constants.js';
import { HoneycombChartProperties } from '../types/honeycomb-config-properties.js';
import {
  HoneycombRequiredConfigProps,
  HoneycombSlotsConfig,
} from '../types/honeycomb-internal.js';
import {
  HoneycombChartAccessorDataProps,
  HoneycombChartNumericalProps,
  HoneycombChartProps,
  HoneycombData,
  HoneycombLabelsDisplay,
  type HoneycombTileData,
  HoneycombTileShape,
} from '../types/honeycomb.js';

const showLabelsToLabelsDisplay = (
  showLabels: boolean,
): HoneycombLabelsDisplay => (showLabels ? 'all' : 'none');

export const isHoneycombChartAccessorDataProps = (
  props: PropsWithChildren<HoneycombChartProps>,
): props is HoneycombChartAccessorDataProps =>
  Array.isArray(props.data) &&
  (props.data.length === 0 ||
    (!isNumber(props.data[0]) && !isString(props.data[0]))) &&
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  !(props as HoneycombChartNumericalProps).colorScheme;

const checkCategoricalDataset = (data: HoneycombData) =>
  Array.isArray(data) &&
  (isString(data[0]) ||
    isHoneycombTileCategoricalData((data[0] ?? {}) as HoneycombTileData));

export const checkNumericalDataset = (
  props: PropsWithChildren<HoneycombChartProps>,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
): props is HoneycombChartNumericalProps =>
  Array.isArray(props.data) &&
  (isNumber(props.data[0]) ||
    isHoneycombTileNumericData((props.data[0] ?? {}) as HoneycombTileData));

export const getDefaultColorScheme = (
  props: PropsWithChildren<HoneycombChartProps>,
  contextConfig: HoneycombChartProperties,
) => {
  if (isHoneycombChartAccessorDataProps(props)) {
    return (
      props.colorPalette ||
      contextConfig.colorScheme ||
      DEFAULT_HONEYCOMB_CONFIG.colorScheme
    );
  }

  const currentColorScheme =
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    (props as HoneycombChartNumericalProps).colorScheme ||
    contextConfig.colorScheme;
  const isCategoricalDataset = checkCategoricalDataset(
    props.data as HoneycombData,
  );
  const isColorPaletteName = typeof currentColorScheme === 'string';
  const isCustomNumericColorScheme = Array.isArray(currentColorScheme);
  const isInvalidColorScheme =
    isUndefined(currentColorScheme) || isEmpty(currentColorScheme);

  if (isInvalidColorScheme) {
    return isCategoricalDataset
      ? DEFAULT_HONEYCOMB_CATEGORICAL_COLOR_PALETTE
      : DEFAULT_HONEYCOMB_CONFIG.colorScheme;
  } else if (
    isColorPaletteName ||
    !isCategoricalDataset ||
    isCustomNumericColorScheme
  ) {
    return currentColorScheme;
  } else {
    const capitalizedCategories: Record<string, string> = {};
    Object.keys(currentColorScheme).forEach((key) => {
      capitalizedCategories[key] = (
        currentColorScheme as Record<string, string>
      )[key];
    });

    return capitalizedCategories;
  }
};

export const buildHoneycombConfig = (
  props: PropsWithChildren<HoneycombChartProps>,
  contextConfig: HoneycombChartProperties,
  slots: HoneycombSlotsConfig,
): HoneycombRequiredConfigProps => {
  const shape: HoneycombTileShape =
    props.shape ?? contextConfig.shape ?? DEFAULT_HONEYCOMB_CONFIG.shape;

  const colorScheme = getDefaultColorScheme(props, contextConfig);

  const isToolbarSet = !!(slots.toolbar || contextConfig.toolbar);

  const toolbar = isToolbarSet
    ? merge(
        {},
        DEFAULT_HONEYCOMB_CONFIG.toolbar,
        contextConfig.toolbar,
        slots.toolbar,
      )
    : undefined;

  const unit = props.unit ?? contextConfig.unit ?? undefined;
  const truncationMode =
    props.truncationMode ??
    contextConfig.truncationMode ??
    DEFAULT_HONEYCOMB_CONFIG.truncationMode;

  const formatter = props.formatter ?? contextConfig.formatter;
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const legacyPropsShowLabels = props.showLabels;
  const fromPropsShowLabels =
    legacyPropsShowLabels !== undefined
      ? showLabelsToLabelsDisplay(legacyPropsShowLabels)
      : undefined;
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const legacyConfigShowLabels = contextConfig.showLabels;
  const fromConfigShowLabels =
    legacyConfigShowLabels !== undefined
      ? showLabelsToLabelsDisplay(legacyConfigShowLabels)
      : undefined;
  const labelsDisplay =
    props.labelsDisplay ??
    fromPropsShowLabels ??
    contextConfig.labelsDisplay ??
    fromConfigShowLabels ??
    DEFAULT_HONEYCOMB_CONFIG.labelsDisplay;
  const textSize =
    props.textSize ?? contextConfig.textSize ?? DEFAULT_TEXT_SIZE;

  const legend = merge(
    {},
    DEFAULT_HONEYCOMB_CONFIG.legend,
    contextConfig.legend,
    slots.legend,
  );

  let min, max;

  if (
    checkNumericalDataset(props) ||
    isHoneycombChartAccessorDataProps(props)
  ) {
    min = props.min ?? contextConfig.min ?? undefined;
    max = props.max ?? contextConfig.max ?? undefined;
  }

  const colorRules = (contextConfig.colorRules || [])
    .filter(isValidColorRule)
    .concat(slots.colorRules || []);

  return {
    shape,
    colorScheme,
    toolbar,
    legend,
    unit,
    formatter,
    truncationMode,
    min,
    max,
    labelsDisplay,
    textSize,
    colorRules,
    tooltip: slots.tooltip ?? contextConfig.tooltip,
  };
};
